import ReactSelect, { Props as ReactSelectProps, components } from 'react-select';

import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';

import styles from './custom-react-select.module.css';
import VirtualizedMenuList from './virtualized-menu-list';

export type CustomReactSelectOptions = {
  wrapperClass?: string;
  clearAllClass?: string;
  disableClearWhenNotFocused?: boolean;
  virtualized?: boolean;
  label?: string;
  // react select props are funky, need to type as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: React.RefObject<any>;
};

// Extra props tunneled through selectProps so stable component refs can access them
type ClearIndicatorExtraProps = {
  clearAllClass?: string;
  disableClearWhenNotFocused?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectInstanceRef?: React.RefObject<any>;
};

const MenuWrapper = (props: React.ComponentProps<typeof components.Menu>) => (
  <div className="react-select-menu-container">
    <components.Menu {...props} />
  </div>
);

const ClearIndicatorWrapper = (props: React.ComponentProps<typeof components.ClearIndicator>) => {
  const { clearAllClass, disableClearWhenNotFocused, selectInstanceRef } = props.selectProps as ReactSelectProps &
    ClearIndicatorExtraProps;

  return (
    <div
      {...props.innerProps}
      className={`${clearAllClass || ''} ${props.innerProps.className}`}
      style={{ ...props.innerProps.style, display: 'flex' }}
      tabIndex={0}
      onMouseDown={(e) => {
        if (disableClearWhenNotFocused && !props.isFocused) return;
        props?.innerProps?.onMouseDown?.(e);
      }}
      onTouchEnd={(e) => {
        if (disableClearWhenNotFocused && !props.isFocused) return;
        props?.innerProps?.onTouchEnd?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          props.clearValue();
          const inputRef = selectInstanceRef?.current?.inputRef;
          if (inputRef) inputRef.focus();
        }
      }}
    >
      <Icon name={ICONS.CLOSE} size={14} />
    </div>
  );
};

const CustomReactSelect = ({
  clearAllClass,
  disableClearWhenNotFocused,
  virtualized,
  wrapperClass,
  label,
  ref,
  ...rest
}: ReactSelectProps & CustomReactSelectOptions) => {
  return (
    <div className={`${styles['custom-react-select-wrapper']} ${wrapperClass || ''}`}>
      {rest?.isSearchable && (
        <div
          data-testid="custom-react-select-search-icon-wrapper"
          className={styles['custom-react-select-search-icon-wrapper']}
        >
          <Icon name={ICONS.SEARCH} />
        </div>
      )}

      {label && <label className="L2">{label}</label>}
      <ReactSelect
        {...rest}
        ref={ref}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({ clearAllClass, disableClearWhenNotFocused, selectInstanceRef: ref } as any)}
        components={{
          ...rest?.components,
          Menu: rest?.components?.Menu || MenuWrapper,
          ClearIndicator: rest?.components?.ClearIndicator || ClearIndicatorWrapper,
          ...(virtualized && !rest?.components?.MenuList ? { MenuList: VirtualizedMenuList } : {}),
        }}
      />
    </div>
  );
};

export default CustomReactSelect;
